import React from "react";

export type DataTableDensity = "comfortable" | "compact";

type DataTableProps = React.TableHTMLAttributes<HTMLTableElement> & {
  density?: DataTableDensity;
};

/**
 * Styled <table> wrapper. Applies the .tb-table class family from primitives.css:
 * uppercase eyebrow column headers, tabular-nums data cells, sticky thead.
 *
 * Use modifier classes on individual cells:
 *   - `is-numeric`  right-align + monospace
 *   - `is-symbol`   bold ticker styling
 *   - `is-pos` / `is-neg` / `is-zero`  semantic PnL coloring
 *   - `is-sortable` on a <th> when wired to a sort handler
 */
export function DataTable({
  density = "comfortable",
  className,
  ...rest
}: DataTableProps) {
  const cls = [
    "tb-table",
    density === "compact" ? "tb-table-compact" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");
  return <table className={cls} {...rest} />;
}

type SortDirection = "asc" | "desc";

type SortHeaderProps = React.ThHTMLAttributes<HTMLTableCellElement> & {
  /** Whether this column is the one currently sorted. */
  active?: boolean;
  /** Sort direction when active. */
  direction?: SortDirection;
  /** Right-align + monospace styling for numeric columns. */
  numeric?: boolean;
};

/**
 * Sortable column header. Renders an `aria-sort`-aware <th> with a tiny
 * direction caret when active.
 */
export function SortHeader({
  active,
  direction = "asc",
  numeric,
  className,
  children,
  ...rest
}: SortHeaderProps) {
  const cls = [
    "is-sortable",
    numeric ? "is-numeric" : "",
    active ? "is-active" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");
  const ariaSort = active
    ? direction === "asc"
      ? "ascending"
      : "descending"
    : "none";
  return (
    <th className={cls} aria-sort={ariaSort} {...rest}>
      <span className="tb-th-label">{children}</span>
      <span className="tb-th-caret" aria-hidden="true">
        {active ? (direction === "asc" ? "▲" : "▼") : ""}
      </span>
    </th>
  );
}
